import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import ReactPlayer from "react-player";
import TranscriptTable from "../components/transcript-table";
import ViewQuestionsModal from "../components/view-questions-modal";
import { toast } from "react-toastify";
import EditPromptModal from "../components/edit-prompt-modal";
import ContentTable from "../components/content-table";
import { isTeacherRole } from "../shared/utils";
import DeleteConfirmationModal from "../components/delete-confirmation-modal";

const VideoGuideSettings = () => {
  const [played, setPlayed] = useState(0); // range: 0.0 - 1.0
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const [questionConfirmation, setQuestionConfirmation] = useState(false);
  const [chapterConfirmation, setChapterConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const togglePlayPause = () => {
    setPlaying((prev) => !prev);
  };

  const handleProgress = (progress) => {
    setPlayed(progress.played);
    setPlayedSeconds(progress.playedSeconds);
    setVideoProgress(progress.playedSeconds);
  };

  const handleDuration = (dur) => {
    setDuration(dur);
  };

  const handleSeekChange = (e) => {
    const newPlayed = parseFloat(e.target.value);
    setPlayed(newPlayed);
    ref.current.seekTo(newPlayed);
  };

  const [sourceType, _setSourceType] = useState("course-content");
  const setSourceType = useCallback((val) => {
    setVideoData();
    _setSourceType(val);
    setUrlInputValue("");
    setSelectedSource({
      lesson: "",
      course: "",
      unit: "",
      url: "",
    });
    setCurrentVideoCode("");
  }, []);

  const [urlInputValue, setUrlInputValue] = useState("");
  const [selectedSource, setSelectedSource] = useState({
    lesson: "",
    course: "",
    unit: "",
    url: "",
  });

  const [currentVideoCode, setCurrentVideoCode] = useState("");
  const [tableView, setTableView] = useState("transcript");

  const [courseArray, setCourseArray] = useState([]);
  const [unitArray, setUnitArray] = useState([]);
  const [lessonArray, setLessonArray] = useState([]);
  const [urlArray, setUrlArray] = useState([]);

  const getVideoName = useCallback(async (videoCode) => {
    try {
      const { data } = await axios.post(
        "https://" +
          process.env.REACT_APP_HOST +
          "/webservice/rest/server.php?wstoken=" +
          process.env.REACT_APP_TOKEN +
          "&wsfunction=local_course_service_getvideodetails&moodlewsrestformat=json&url=" +
          toVideoUrl(videoCode)
      );
      let dataArr = [];
      try {
        dataArr = Object.values(JSON.parse(data.data));
      } catch (err) {}
      if (dataArr.length) {
        const urlData = dataArr[0];
        return urlData.video_title;
      } else {
        return videoCode;
      }
    } catch (err) {
      return videoCode;
    }
  }, []);

  const [videoData, setVideoData] = useState();
  const [loading, setLoading] = useState(false);
  const searchVideo = useCallback(async () => {
    const videoCode =
      sourceType === "course-content"
        ? selectedSource.url
        : extractYouTubeVideoIds(urlInputValue)[0];

    setVideoData();
    setLoading(true);
    try {
      const { data } = await axios.get(
        process.env.REACT_APP_API_URL +
          `/video_guide/chapter_generate?video_url=${toVideoUrl(videoCode)}`
      );
      if (data.success) {
        setVideoData({
          added: data.message.queue_status === "Completed",
          published: +data.message.publish === 1,
          videoCode,
          queueStatus: data.message.queue_status,
          message: data.message.message,
        });
        setCurrentVideoCode(videoCode);
      }
    } catch (err) {
      const queueStatus = err?.response?.data?.message?.queue_status;
      const message =
        err?.response?.data?.message?.message ?? err?.response?.data?.message;
      if (
        err?.response?.data?.success === false &&
        (!["Started", "InProgress", "Processing"].includes(queueStatus) ||
          queueStatus === "Failed")
      ) {
        if (sourceType === "url") {
          const { data } = await axios.post(
            "https://" +
              process.env.REACT_APP_HOST +
              "/webservice/rest/server.php?wstoken=" +
              process.env.REACT_APP_TOKEN +
              "&wsfunction=local_course_service_getvideodetails&moodlewsrestformat=json&url=" +
              toVideoUrl(videoCode)
          );
          let dataArr = [];
          try {
            dataArr = Object.values(JSON.parse(data.data));
          } catch (err) {}
          if (dataArr.length) {
            const urlData = dataArr[0];
            setVideoData({
              added: false,
              published: false,
              videoCode,
              courseId: urlData.course_id,
              unitId: urlData.unit_id,
              lessonId: urlData.lesson_id,
              message,
            });
          } else {
            toast.error("Video does not exist in the database");
          }
        } else {
          setVideoData({
            added: false,
            published: false,
            videoCode,
            courseId: selectedSource.course,
            unitId: selectedSource.unit,
            lessonId: selectedSource.lesson,
            message,
          });
        }
      } else if (
        ["Started", "InProgress", "Processing"].includes(queueStatus)
      ) {
        const { data } = err.response;
        setVideoData({
          added: false,
          published: +data.message.publish === 1,
          videoCode,
          queueStatus: queueStatus,
          message,
        });
      } else {
        setVideoData({
          added: false,
          published: false,
          videoCode,
          message,
        });
      }
      console.log(err);
    } finally {
      setLoading(false);
      setEnableChapterRegeneration(false);
    }
  }, [sourceType, selectedSource, urlInputValue]);

  const [addingVideo, setAddingVideo] = useState(false);
  const addVideo = useCallback(() => {
    if (!videoData) return;

    setAddingVideo(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/video_guide/chapter_generate`, {
        video_url: toVideoUrl(videoData.videoCode),
        course_id: videoData.courseId,
        unit_id: videoData.unitId,
        activity_id: videoData.lessonId,
      })
      .then(({ data }) => {
        if (data.queue_status === "Completed") {
          searchVideo();
        } else {
          setVideoData((prev) => ({
            ...prev,
            added: false,
            queueStatus: "Started",
          }));
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error("Error occurred while adding the video.");
      })
      .finally(() => {
        setAddingVideo(false);
      });
  }, [videoData]);

  const [publishingVideo, setPublishingVideo] = useState(false);
  const publishVideo = useCallback(
    (status) => {
      if (!videoData) return;
      setPublishingVideo(true);
      axios
        .put(`${process.env.REACT_APP_API_URL}/video_guide/chapter_generate`, {
          video_url: toVideoUrl(videoData.videoCode),
          publish: status,
        })
        .then(({ data }) => {
          setVideoData((prev) => ({
            ...prev,
            published: status,
          }));
          searchVideo();
          toast.success(
            status
              ? "Successfully added a video guide link on the course unit page."
              : "Successfully removed the video guide link on the course unit page."
          );
        })
        .catch((err) => {
          console.log(err);
          toast.error(
            `Error occurred while ${
              status ? "publishing" : "unpublishing"
            } the video guide.`
          );
        })
        .finally(() => {
          setPublishingVideo(false);
        });
    },
    [videoData]
  );

  const validateUnits = (data) => {
    const temp = data.slice();
    const val = temp.filter(
      (unit) => unit.name.trim() !== "" && unit.name != "."
    );
    return val;
  };

  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showPromptsModal, setPromptsModal] = useState(false);

  const isTeacher = useMemo(() => isTeacherRole(), []);

  useEffect(() => {
    let url =
      "https://" +
      process.env.REACT_APP_HOST +
      "/webservice/rest/server.php?wstoken=" +
      process.env.REACT_APP_TOKEN +
      "&wsfunction=local_course_service_course_data&moodlewsrestformat=json";

    if (isTeacher) {
      const userId = localStorage.getItem("user_id");
      url += "&user_id=" + userId;
    }

    axios
      .post(url)
      .then((response) => {
        setCourseArray(response.data.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);
  useEffect(() => {
    if (!selectedSource.course) return;
    axios
      .post(
        "https://" +
          process.env.REACT_APP_HOST +
          "/webservice/rest/server.php?wstoken=" +
          process.env.REACT_APP_TOKEN +
          "&wsfunction=local_course_service_course_unit_data&moodlewsrestformat=json&course_id=" +
          selectedSource.course
      )
      .then((response) => {
        if (response.data) {
          const decodedData = JSON.parse(response.data.data);
          let units = decodedData[0].units;
          setUnitArray(validateUnits(units));
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [selectedSource.course]);
  useEffect(() => {
    if (!selectedSource.unit) return;
    axios
      .post(
        "https://" +
          process.env.REACT_APP_HOST +
          "/webservice/rest/server.php?wstoken=" +
          process.env.REACT_APP_TOKEN +
          "&wsfunction=local_course_service_unit_lessions_data&moodlewsrestformat=json&unit_id=" +
          selectedSource.unit
      )
      .then((response) => {
        if (response.data) {
          setLessonArray(response.data.data);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [selectedSource.unit]);
  useEffect(() => {
    if (!selectedSource.lesson) return;
    const lesson = lessonArray.find(
      (l) => l.Id.toString() === selectedSource.lesson
    );
    if (!lesson) return;
    const urls = extractYouTubeVideoIds(lesson.Lession_Content);
    setUrlArray([]);
    Promise.all(urls.map((url) => getVideoName(url))).then((names) => {
      setUrlArray(
        urls.map((url, i) => ({
          url,
          name: names[i],
        }))
      );
    });
  }, [selectedSource.lesson]);

  const [regeneratingChapter, setRegeneratingChapter] = useState(false);

  const regenerateQuestions = useCallback(
    (videoCode) => {
      setDeleting(true);
      let url = `${process.env.REACT_APP_API_URL}/video_guide`;
      // if (tableView === "transcript") {
      //   url = `${process.env.REACT_APP_API_URL}/video_guide_re_chapter`;
      // } else if (tableView === "content") {
      //   url = `${process.env.REACT_APP_API_URL}/video_guide`;
      // }
      if (!url) return;
      setRegeneratingChapter(true);
      axios
        .put(url, { video_url: `https://www.youtube.com/embed/${videoCode}` })
        .then((response) => {
          toast.success("Regeneration successful. Check new questions.");
          searchVideo();
          setCurrentVideoCode("");
          setTimeout(() => setCurrentVideoCode(videoCode), 1000);
        })
        .catch((err) => {
          toast.error("Error occured while regenerating chapter.");
          console.log(err);
        })
        .finally(() => {
          setDeleting(false);
          setQuestionConfirmation(false);
          setRegeneratingChapter(false);
        });
    },
    [tableView, searchVideo]
  );

  const ref = useRef();
  const scrollRef = useRef();

  const [enableChapterRegeneration, setEnableChapterRegeneration] =
    useState(false);

  const [playing, setPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const timeout = useRef();

  const chapterRegenerator = useCallback(
    (videoCode) => {
      // setRegeneratingChapter(true);
      setDeleting(true);
      let url = `${process.env.REACT_APP_API_URL}/video_guide_re_chapter`;
      axios
        .put(url, { video_url: `https://www.youtube.com/embed/${videoCode}` })
        .then((response) => {
          toast.success("Regeneration successful. Check new questions.");
          searchVideo();
          setCurrentVideoCode("");
          setTimeout(() => setCurrentVideoCode(videoCode), 1000);
        })
        .catch((err) => {
          toast.error("Error occured while regenerating chapter.");
          console.log(err);
        })
        .finally(() => {
          // setRegeneratingChapter(false);
          setDeleting(false);
          setChapterConfirmation(false);
        });
    },
    [tableView, searchVideo]
  );

  return (
    <>
      <Container className="my-3">
        <div className="d-flex gap-4 mb-2 align-items-center">
          <Form.Check
            type="radio"
            name="sourceType"
            checked={sourceType === "url"}
            onChange={(e) => setSourceType("url")}
            label="Direct URL"
          />
          <Form.Check
            type="radio"
            name="sourceType"
            checked={sourceType === "course-content"}
            onChange={(e) => setSourceType("course-content")}
            label="Lesson"
          />
          <div
            style={{
              textAlign: "left",
              alignItems: "center",
              marginLeft: "auto",
            }}
          >
            {videoData?.message ? (
              <>
                <span style={{ fontWeight: "bold" }}>Status: </span>
                {videoData?.message}
              </>
            ) : (
              ""
            )}
          </div>
        </div>
        <Row ref={scrollRef} className="align-items-center">
          {sourceType === "url" && (
            <Col md="6" lg="8s">
              <Form.Group
                controlId="url-textbox"
                className="d-flex align-items-center"
              >
                <Form.Control
                  type="text"
                  placeholder="Enter URL"
                  style={{ maxWidth: "100%" }}
                  value={urlInputValue}
                  onChange={(e) => setUrlInputValue(e.target.value)}
                />
              </Form.Group>
            </Col>
          )}
          {sourceType === "course-content" && (
            <>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.course}
                    onChange={(e) =>
                      setSelectedSource((s) => ({
                        ...s,
                        course: e.target.value,
                      }))
                    }
                  >
                    <option>Select Course</option>
                    {!!courseArray &&
                      courseArray.map((course) => (
                        <option key={course.Course_ID} value={course.Course_ID}>
                          {course.Course_Name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.unit}
                    onChange={(e) =>
                      setSelectedSource((s) => ({ ...s, unit: e.target.value }))
                    }
                  >
                    <option>Select Unit</option>
                    {!!unitArray &&
                      unitArray.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.lesson}
                    onChange={(e) =>
                      setSelectedSource((s) => ({
                        ...s,
                        lesson: e.target.value,
                      }))
                    }
                  >
                    <option>Select Lesson</option>
                    {!!lessonArray &&
                      lessonArray.map((lesson) => (
                        <option key={lesson.Id} value={lesson.Id}>
                          {lesson.Lession_Name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="6" lg="4">
                <Form.Group className="py-1" controlId="source">
                  <Form.Select
                    value={selectedSource.url}
                    onChange={(e) =>
                      setSelectedSource((s) => ({ ...s, url: e.target.value }))
                    }
                  >
                    <option>Select Video</option>
                    {!!urlArray &&
                      urlArray.map((url) => (
                        <option key={url.url} value={url.url}>
                          {url.name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </>
          )}
          <Col md="6" lg="4" style={{ display: "flex", gap: "8px" }}>
            <Button
              variant="outline-dark"
              disabled={
                sourceType === "course-content"
                  ? !selectedSource.url
                  : !urlInputValue
              }
              onClick={() => searchVideo()}
              style={{ height: "48px" }}
            >
              Search
            </Button>
            <Button
              variant="outline-dark"
              onClick={() => {
                setSelectedSource({
                  lesson: "",
                  course: "",
                  unit: "",
                  url: "",
                });
                setUrlInputValue("");
                setVideoData();
              }}
              style={{ height: "48px" }}
            >
              Clear
            </Button>
          </Col>
        </Row>
        {loading && <div style={{ margin: "40px 0" }}> Loading</div>}
        {!!videoData && !videoData.added && (
          <div className="d-flex gap-2 mb-2" style={{ textAlign: "left" }}>
            {["Started", "InProgress", "Processing"].includes(
              videoData.queueStatus
            ) ? (
              "The selected video is being prepared for the Video Guide, and backend AI processing has started. This process may take a few minutes. Once the status changes to 'Ready for Review', you can begin reviewing the content and questions."
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  addVideo();
                }}
                style={{ height: "48px" }}
                disabled={addingVideo}
              >
                {addingVideo ? (
                  <div className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    Adding Video
                  </div>
                ) : (
                  "Add Video"
                )}
              </Button>
            )}
          </div>
        )}
        {!!videoData && videoData.added && (
          <>
            <div className="d-flex gap-2 mt-1 mb-2 align-items-center">
              <Button
                variant="primary"
                onClick={() => {
                  if (sourceType === "course-content") {
                    setCurrentVideoCode(selectedSource.url);
                  } else {
                    const videoCodes = extractYouTubeVideoIds(urlInputValue);
                    if (videoCodes.length) setCurrentVideoCode(videoCodes[0]);
                  }
                  setTableView("transcript");
                }}
                disabled={
                  sourceType === "course-content"
                    ? !selectedSource.url
                    : !urlInputValue
                }
                style={{ height: "48px" }}
                className={tableView === "transcript" ? "btn-active" : ""}
              >
                Video Transcript
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (sourceType === "course-content") {
                    setCurrentVideoCode(selectedSource.url);
                  } else {
                    const videoCodes = extractYouTubeVideoIds(urlInputValue);
                    if (videoCodes.length) setCurrentVideoCode(videoCodes[0]);
                  }
                  setTableView("content");
                }}
                disabled={
                  sourceType === "course-content"
                    ? !selectedSource.url
                    : !urlInputValue
                }
                style={{ height: "48px" }}
                className={tableView === "content" ? "btn-active" : ""}
              >
                Chapter Content
              </Button>
              {!isTeacher && (
                <Button
                  variant="primary"
                  onClick={() => setPromptsModal(true)}
                  style={{ height: "48px" }}
                  disabled={
                    sourceType === "course-content"
                      ? !selectedSource.url
                      : !urlInputValue
                  }
                >
                  Edit Prompt
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => {
                  setShowQuestionsModal(true);
                }}
                style={{ height: "48px" }}
                disabled={
                  sourceType === "course-content"
                    ? !selectedSource.url
                    : !urlInputValue
                }
              >
                View Questions
              </Button>
              <Button
                variant="primary"
                onClick={() => setQuestionConfirmation(true)}
                style={{ height: "48px" }}
                // disabled={
                //   !enableChapterRegeneration ||
                //   regeneratingChapter ||
                //   !tableView ||
                //   (sourceType === "course-content"
                //     ? !selectedSource.url
                //     : !urlInputValue)
                // }
              >
                {regeneratingChapter ? (
                  <div className="d-flex align-items-center gap-2 ms-2">
                    <Spinner animation="border" size="sm" />
                    Loading
                  </div>
                ) : (
                  "Questions Regenerator"
                )}
              </Button>
              <Button
                variant="primary"
                onClick={() => setChapterConfirmation(true)}
              >
                Chapter Regenerator
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  publishVideo(!videoData.published);
                }}
                style={{ height: "48px" }}
                disabled={
                  (!videoData.published &&
                    !videoData.message.includes("ready for publish")) ||
                  publishingVideo
                }
              >
                {!videoData.published ? "Publish" : "Unpublish"}
              </Button>
            </div>
            <Row>
              <Col xs="12">
                {!!currentVideoCode && (
                  // <ReactPlayer
                  //   ref={ref}
                  //   controls
                  //   url={`https://www.youtube.com/watch?v=${currentVideoCode}`}
                  //   width={"100%"}
                  //   playing={playing}
                  //   onPlay={() => {
                  //     if (!playing) {
                  //       clearTimeout(timeout.current);
                  //       setPlaying(true);
                  //     }
                  //   }}
                  //   onPause={() => {
                  //     if (playing) {
                  //       clearTimeout(timeout.current);
                  //       setPlaying(false);
                  //     }
                  //   }}
                  //   onProgress={({ playedSeconds }) => {
                  //     setVideoProgress(playedSeconds);
                  //   }}
                  // />
                  <div
                    style={{
                      maxWidth: "1200px",
                      margin: "auto",
                      padding: "20px",
                    }}
                  >
                    <ReactPlayer
                      ref={ref}
                      url={`https://www.youtube.com/watch?v=${currentVideoCode}`}
                      playing={playing}
                      controls={false}
                      onProgress={handleProgress}
                      onDuration={handleDuration}
                      width="100%"
                      height="400px"
                    />

                    {/* Custom Controls */}
                    <div
                      style={{
                        background: "#111",
                        color: "#fff",
                        padding: "10px",
                        marginTop: "10px",
                        borderRadius: "6px",
                      }}
                    >
                      <button
                        onClick={togglePlayPause}
                        style={{ marginRight: "10px" }}
                      >
                        {playing ? "Pause" : "Play"}
                      </button>

                      <input
                        type="range"
                        min={0}
                        max={1}
                        step="0.01"
                        value={played}
                        onChange={handleSeekChange}
                        style={{ width: "60%", verticalAlign: "middle" }}
                      />

                      <span style={{ marginLeft: "10px" }}>
                        {/* {Math.floor(playedSeconds * 1000)}ms / {Math.floor(duration * 1000)}ms */}
                        {playedSeconds.toFixed(3)}s / {duration.toFixed(3)}s
                      </span>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
            {tableView === "transcript" && (
              <TranscriptTable
                videoCode={currentVideoCode}
                scrollRef={scrollRef}
                onPlayClick={(startSeconds, endSeconds) => {
                  ref.current.seekTo(startSeconds, "seconds");
                  setPlaying(true);
                  clearTimeout(timeout.current);
                  timeout.current = setTimeout(() => {
                    setPlaying(false);
                  }, (endSeconds - startSeconds) * 1000);
                }}
                videoProgress={videoProgress}
                enableChapterRegeneration={() =>
                  setEnableChapterRegeneration(true)
                }
              />
            )}
            {tableView === "content" && (
              <ContentTable
                videoCode={currentVideoCode}
                scrollRef={scrollRef}
                onPlayClick={(startSeconds, endSeconds) => {
                  ref.current.seekTo(startSeconds, "seconds");
                  setPlaying(true);
                  clearTimeout(timeout.current);
                  timeout.current = setTimeout(() => {
                    setPlaying(false);
                  }, (endSeconds - startSeconds) * 1000);
                }}
                videoProgress={videoProgress}
                enableChapterRegeneration={() =>
                  setEnableChapterRegeneration(true)
                }
              />
            )}
            {showQuestionsModal && (
              <ViewQuestionsModal
                onClose={() => setShowQuestionsModal(false)}
                videoCode={
                  sourceType === "course-content"
                    ? selectedSource.url
                    : extractYouTubeVideoIds(urlInputValue)[0]
                }
              />
            )}
            {showPromptsModal && (
              <EditPromptModal
                onClose={() => setPromptsModal(false)}
                videoCode={
                  sourceType === "course-content"
                    ? selectedSource.url
                    : extractYouTubeVideoIds(urlInputValue)[0]
                }
                enableChapterRegeneration={() =>
                  setEnableChapterRegeneration(true)
                }
              />
            )}
            {(questionConfirmation || chapterConfirmation) && (
              <DeleteConfirmationModal
                loading={deleting}
                onClose={() =>
                  questionConfirmation
                    ? setQuestionConfirmation(false)
                    : setChapterConfirmation(false)
                }
                onConfirm={() =>
                  questionConfirmation
                    ? regenerateQuestions(
                        sourceType === "course-content"
                          ? selectedSource.url
                          : extractYouTubeVideoIds(urlInputValue)[0]
                      )
                    : chapterRegenerator(
                        sourceType === "course-content"
                          ? selectedSource.url
                          : extractYouTubeVideoIds(urlInputValue)[0]
                      )
                }
                description={
                  questionConfirmation
                    ? "The existing question might change when regenerating questions. Are you sure you want to proceed?"
                    : "The existing question might change when regenerating the chapter. Are you sure you want to proceed?"
                }
              />
            )}
          </>
        )}
      </Container>
    </>
  );
};

export function extractYouTubeVideoIds(text) {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/g;
  const videoIds = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    videoIds.push(match[1]);
  }

  return videoIds;
}

export function toVideoUrl(videoCode) {
  return `https://www.youtube.com/embed/${videoCode}`;
}

export default VideoGuideSettings;
